import { clsx } from 'clsx';
import { TabsContent } from 'src/components/ui/tabs';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTheme } from 'src/provider/ThemeProvider';
import { useNotification } from 'src/hooks/useNotification';
import {
  ConfigurationSettings,
  useAssignProjectKnowledgeBaseMutation,
  useGetKnowledgeBaseQuery,
  useGetProjectByIdQuery,
  useUnassignProjectKnowledgeBaseMutation,
} from 'src/services';
import { ProductsTypeBack } from 'src/constants/products';
import { useAppSelector } from 'src/store';
import { LinkToKnowledgeBase } from 'src/pages/ConfigurationPage/DataSourcesTab/LinkToKnowledgeBase/LinkToKnowledgeBase';
import { KnowledgeBaseLabel } from 'src/pages/ConfigurationPage/DataSourcesTab/KnowledgeBaseLabel/KnowledgeBaseLabel';
import { SwitchKBComponent } from 'src/pages/ControlDeskPage/DataSourceSection/KnowledgeBases/AssignKnowledgeBasePopup/SwitchKBComponent';
import { CreateKnowledgeBaseModal } from 'src/pages/ConfigurationPage/DataSourcesTab/CreateKnowledgeBaseModal/CreateKnowledgeBaseModal';
import type { KnowledgeBase } from 'src/services/knowledgeBaseService';
import { assignedFirst } from 'src/pages/ConfigurationPage/DataSourcesTab/assignedFirst';
import { useLoaderWhile } from 'src/hooks/useLoaderWhile';
import { getApiVersion } from 'src/utils/apiVersion';
import { API_VERSION } from 'src/constants/api-routes';
import { CustomConfigurationMessage } from 'src/components/CustomConfigurationMessage/CustomConfigurationMessage';

export const DataSourcesTab = ({
  readonly,
  settings,
  isActiveTab = false,
}: {
  readonly: boolean;
  settings: ConfigurationSettings[];
  isActiveTab?: boolean;
}) => {
  const [isCustomDeployedSettings, setIsCustomDeployedSettings] = useState<boolean>(false);
  const [knowledgeBases, setKnowledgeBases] = useState<KnowledgeBase[]>([]);
  const { id } = useParams();
  const { config } = useTheme();
  const version = getApiVersion();
  const isV2 = version === API_VERSION.V2;
  const notification = useNotification();

  useEffect(() => {
    if (settings) {
      const isCustom = Boolean(settings.find((item) => item.is_deployed)?.is_custom);

      setIsCustomDeployedSettings(isCustom);
    }
  }, [settings]);

  const { data: currentProject, isFetching: isProjectFetching } = useGetProjectByIdQuery(
    {
      id: id,
    },
    { skip: !isActiveTab },
  );

  const { data: knowledgeBaseData, isFetching: isFetchingKnowledgeBaseData } =
    useGetKnowledgeBaseQuery(
      {
        load_projects: true,
        page_size: 100,
      },
      { refetchOnMountOrArgChange: true, skip: !isActiveTab },
    );

  const [assignKB, { isLoading: assignKBisLoading, isSuccess: assignKBisSuccess }] =
    useAssignProjectKnowledgeBaseMutation();
  const [unassignKB, { isLoading: unassignKBIsLoading, isSuccess: unassignKBisSuccess }] =
    useUnassignProjectKnowledgeBaseMutation();

  useLoaderWhile(isFetchingKnowledgeBaseData, isProjectFetching);

  useEffect(() => {
    if (assignKBisSuccess) {
      notification.success('Knowledge Base has been assigned.');
    }
  }, [assignKBisSuccess, assignKBisLoading]);

  useEffect(() => {
    if (unassignKBisSuccess) {
      notification.success('Knowledge Base has been unassigned.');
    }
  }, [unassignKBisSuccess, unassignKBIsLoading]);

  useEffect(() => {
    if (knowledgeBaseData) {
      setKnowledgeBases(assignedFirst(knowledgeBaseData.items, currentProject.id));
    }
  }, [knowledgeBaseData]);

  const handleChangeSwitch = (val: boolean, id: KnowledgeBase['id']) => {
    if (val) {
      assignKB({
        project_type: ProductsTypeBack[currentProject?.project_type],
        projectId: currentProject.id,
        body: [id],
      });
    }
    if (!val) {
      unassignKB({
        project_type: ProductsTypeBack[currentProject?.project_type],
        projectId: currentProject.id,
        body: [id],
      });
    }
  };

  const onCreateKB = (id: number) => {
    assignKB({
      project_type: ProductsTypeBack[currentProject?.project_type],
      projectId: currentProject.id,
      body: [id],
    });
  };

  const { isInactivePlan } = useAppSelector((s) => s.planSlice);

  return (
    <TabsContent className={clsx('p-2 mt-0 h-full overflow-auto pr-2 pl-0')} value={'data-sources'}>
      {isCustomDeployedSettings && isV2 ? (
        <CustomConfigurationMessage className="w-full" />
      ) : (
        <div className="flex flex-col items-start gap-3.5 w-full">
          <div className="">
            <p className={clsx(config?.textColorClassName, 'font-bold text-sm mb-1')}>
              Knowledge Bases
            </p>
          </div>
          <div className="flex flex-col items-start gap-3.5 w-full">
            {knowledgeBases?.length ? (
              <>
                {knowledgeBases?.map((item) => {
                  return (
                    <div
                      className={clsx(
                        (isProjectFetching || isFetchingKnowledgeBaseData) &&
                          'blur-sm animate-pulse pointer-events-none',
                        'w-full pb-4 border-b border-light flex justify-between items-center',
                      )}
                      key={item.id}
                    >
                      <p
                        title={item.name}
                        className="text-sm font-semibold text-ebony truncate pointer-events-none"
                      >
                        {item.name}
                      </p>
                      <div className="flex sm:gap-7.5 gap-2 items-center sm:justify-end justify-between">
                        <LinkToKnowledgeBase
                          isInactivePlan={isInactivePlan}
                          isProjectActive={currentProject.is_active}
                          knowledgeBaseId={item.id}
                        />
                        <div className={'flex items-center justify-center'}>
                          {readonly ? (
                            <KnowledgeBaseLabel
                              enabled={item.projects.some(
                                (project) => project.id === currentProject.id,
                              )}
                            />
                          ) : (
                            <SwitchKBComponent
                              defaultChecked={item.projects.some(
                                (project) => project.id === currentProject.id,
                              )}
                              onCheckedChange={(value) => {
                                handleChangeSwitch(value, item.id);
                              }}
                              isFetching={assignKBisLoading || unassignKBIsLoading}
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </>
            ) : (
              <p
                className={clsx(
                  isFetchingKnowledgeBaseData && 'blur-sm animate-pulse pointer-events-none',
                  'opacity-30 font-jamjuree font-medium text-sm truncate',
                )}
              >
                AI is not connected to any knowledge base
              </p>
            )}
            <CreateKnowledgeBaseModal disabled={isInactivePlan} onCreate={onCreateKB} />
          </div>
        </div>
      )}
    </TabsContent>
  );
};
